import React, { useContext, useState, useCallback, useEffect } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  FaShoppingCart,
  FaCheckCircle,
  FaBoxOpen,
  FaClock,
  FaShieldAlt,
  FaArrowLeft,
  FaTruck,
  FaStar,
} from "react-icons/fa";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { findCategoryHierarchy } from "../utils/findCategoryHierarchy";

/* =========================================================
   HELPERS
========================================================= */

const hasValue = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
};

const cleanList = (items) => {
  if (!Array.isArray(items)) return [];

  return items.filter(
    (item) =>
      item !== null &&
      item !== undefined &&
      String(item).trim() !== "",
  );
};

/* =========================================================
   READ MORE LIST
========================================================= */

const ReadMoreList = ({
  items,
  field,
  label,
  showMore,
  toggleShowMore,
}) => {
  const cleaned = cleanList(items);

  if (cleaned.length === 0) return null;

  const maxItems = 4;
  const shouldShowReadMore = cleaned.length > maxItems;

  const displayedItems = showMore[field]
    ? cleaned
    : cleaned.slice(0, maxItems);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-5 bg-[#35A93E] rounded-full" />

        <h2 className="text-sm font-bold text-gray-900">
          {label}
        </h2>
      </div>

      <ul className="text-xs text-gray-600 list-disc pl-5 space-y-2">
        {displayedItems.map((item, index) => (
          <li key={index} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>

      {shouldShowReadMore && (
        <button
          type="button"
          onClick={() => toggleShowMore(field)}
          className="text-[#35A93E] font-semibold hover:underline mt-3 text-xs"
        >
          {showMore[field]
            ? "Show Less ▲"
            : "Read More ▼"}
        </button>
      )}
    </div>
  );
};

/* =========================================================
   VARIATIONS
========================================================= */

const VariationOptions = ({
  variations,
  selectedVariation,
  onSelectVariation,
}) => {
  if (!variations?.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
      <h3 className="font-bold text-gray-900 mb-3 text-sm">
        Select Option
      </h3>

      <div className="flex flex-wrap gap-2">
        {variations.map((variation) => {
          const parts = [];

          if (hasValue(variation.size)) {
            parts.push(`Size: ${variation.size}`);
          }

          if (hasValue(variation.color)) {
            parts.push(`Color: ${variation.color}`);
          }

          const label =
            parts.length > 0
              ? parts.join(" • ")
              : "Standard";

          return (
            <button
              type="button"
              key={variation._id}
              onClick={() => onSelectVariation(variation)}
              className={`px-4 py-2 text-xs font-semibold rounded-xl border transition-all ${
                selectedVariation?._id === variation._id
                  ? "border-[#35A93E] bg-green-50 text-[#2d8f35] shadow-sm"
                  : "border-gray-200 text-gray-700 hover:border-[#35A93E] hover:bg-green-50"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* =========================================================
   DETAIL ROW
========================================================= */

const DetailRow = ({ label, value }) => {
  if (!hasValue(value)) return null;

  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-gray-100 last:border-0 text-xs">
      <span className="text-gray-500">
        {label}
      </span>

      <span className="font-semibold text-gray-900 text-right max-w-[60%]">
        {value}
      </span>
    </div>
  );
};

/* =========================================================
   PRODUCT DETAILS
========================================================= */

const ProductDetails = () => {
  const { id } = useParams();

  const location = useLocation();

  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const {
    product,
    category,
    subCategory,
    subSubCategory,
    subSubSubCategory,
  } = location.state || {};

  const {
    convertPrice,
    getCurrencySymbol,
  } = useContext(CurrencyContext);

  /* =======================================================
     FETCH CATEGORIES
  ======================================================= */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${base_url}product/getCategory?page=1&limit=100000&sortingColumn=createdAt&sortingOrder=asc`,
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error! status: ${response.status}`,
          );
        }

        const data = await response.json();

        setCategories(data?.data || []);
      } catch (err) {
        console.error(err);

        setError(
          err?.message || "Something went wrong",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  /* =======================================================
     CATEGORY HIERARCHY
  ======================================================= */

  const hierarchy = findCategoryHierarchy(
    categories,
    {},
    id,
  );

  /* =======================================================
     FIND PRODUCT
  ======================================================= */

  const displayProduct =
    product ||
    categories
      ?.flatMap((cat) =>
        (cat.subCategories || []).flatMap(
          (subCat) => [
            ...(subCat.products || []),

            ...(subCat.subSubCategories || []).flatMap(
              (subSubCat) => [
                ...(subSubCat.products || []),

                ...(subSubCat.subSubSubCategories || []).flatMap(
                  (subSubSubCat) =>
                    subSubSubCat.products || [],
                ),
              ],
            ),
          ],
        ),
      )
      .find((p) => p._id === id);

  /* =======================================================
     LOAD PRODUCT INTO STATE
  ======================================================= */

  useEffect(() => {
    if (
      displayProduct &&
      !product &&
      !loading
    ) {
      navigate(`/product-details/${id}`, {
        state: {
          product: displayProduct,
          category: hierarchy.category,
          subCategory: hierarchy.subCategory,
          subSubCategory:
            hierarchy.subSubCategory,
          subSubSubCategory:
            hierarchy.subSubSubCategory,
        },
        replace: true,
      });
    }
  }, [
    displayProduct,
    product,
    loading,
    navigate,
    id,
    hierarchy,
  ]);

  /* =======================================================
     PRODUCT NOT FOUND
  ======================================================= */

  useEffect(() => {
    if (!displayProduct && !loading) {
      navigate("/", {
        replace: true,
      });
    }
  }, [
    displayProduct,
    loading,
    navigate,
  ]);

  /* =======================================================
     PRODUCT TYPE
  ======================================================= */

  const isDigital =
    displayProduct?.productType === "digital";

  const isPhysical = !isDigital;

  /* =======================================================
     VARIATION
  ======================================================= */

  const [
    selectedVariation,
    setSelectedVariation,
  ] = useState(
    displayProduct?.variations?.[0] || null,
  );

  /* =======================================================
     SELECTED IMAGE
  ======================================================= */

  const [
    selectedImage,
    setSelectedImage,
  ] = useState(
    displayProduct?.variations?.[0]?.images?.[0] ||
      displayProduct?.images?.[0] ||
      null,
  );

  useEffect(() => {
    if (!displayProduct) return;

    const firstVariation =
      displayProduct?.variations?.[0] || null;

    setSelectedVariation(firstVariation);

    setSelectedImage(
      firstVariation?.images?.[0] ||
        displayProduct?.images?.[0] ||
        null,
    );
  }, [displayProduct]);

  /* =======================================================
     SHOW MORE
  ======================================================= */

  const [showMore, setShowMore] = useState({
    benefits: false,
    features: false,
    instructionsForUse: false,
    batteryInfo: false,
    operatingConditions: false,
    packageContents: false,
    precautions: false,
    powerRequirements: false,
  });

  /* =======================================================
     LOGIN TOKEN
  ======================================================= */

  const [token, setToken] = useState(() => {
    try {
      const storedUser =
        localStorage.getItem("user_website");

      return storedUser
        ? JSON.parse(storedUser)
        : null;
    } catch (error) {
      console.error(
        "Unable to read login data:",
        error,
      );

      return null;
    }
  });

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const user =
          localStorage.getItem("user_website");

        setToken(
          user ? JSON.parse(user) : null,
        );
      } catch (error) {
        console.error(
          "Unable to read login data:",
          error,
        );

        setToken(null);
      }
    };

    window.addEventListener(
      "storage",
      handleStorageChange,
    );

    return () =>
      window.removeEventListener(
        "storage",
        handleStorageChange,
      );
  }, []);

  /* =======================================================
     REVIEW STATE
  ======================================================= */

  const [
    selectedRating,
    setSelectedRating,
  ] = useState(0);

  const [
    reviewText,
    setReviewText,
  ] = useState("");

  const [
    reviewLoading,
    setReviewLoading,
  ] = useState(false);

  const [
    reviewImages,
    setReviewImages,
  ] = useState([]);

  const [
    reviewImagePreviews,
    setReviewImagePreviews,
  ] = useState([]);

  const [
    localReviews,
    setLocalReviews,
  ] = useState([]);

  const [
    loadedProductId,
    setLoadedProductId,
  ] = useState(null);

  /* =======================================================
     SYNC REVIEWS ONLY WHEN PRODUCT ID CHANGES
  ======================================================= */

  useEffect(() => {
    if (!displayProduct?._id) return;

    if (
      loadedProductId === displayProduct._id
    ) {
      return;
    }

    const active = (
      displayProduct.reviews || []
    ).filter(
      (r) => r.isActive !== false,
    );

    setLocalReviews(active);

    setLoadedProductId(
      displayProduct._id,
    );
  }, [
    displayProduct,
    loadedProductId,
  ]);

  /* =======================================================
     REVIEW CREATE URL
  ======================================================= */

  const reviewCreateUrl =
    `${base_url}v1/review/create`;

  /* =======================================================
     REVIEW IMAGE HANDLERS
  ======================================================= */

  const handleReviewImageChange = (e) => {
    const files = Array.from(
      e.target.files || [],
    );

    const remainingSlots =
      5 - reviewImages.length;

    const selectedFiles =
      files.slice(0, remainingSlots);

    if (selectedFiles.length === 0) {
      return;
    }

    const newPreviews =
      selectedFiles.map((file) =>
        URL.createObjectURL(file),
      );

    setReviewImages((prev) => [
      ...prev,
      ...selectedFiles,
    ]);

    setReviewImagePreviews((prev) => [
      ...prev,
      ...newPreviews,
    ]);

    e.target.value = "";
  };

  const removeReviewImage = (index) => {
    setReviewImages((prev) =>
      prev.filter(
        (_, i) => i !== index,
      ),
    );

    setReviewImagePreviews((prev) => {
      if (prev[index]) {
        URL.revokeObjectURL(
          prev[index],
        );
      }

      return prev.filter(
        (_, i) => i !== index,
      );
    });
  };

  /* =======================================================
     SUBMIT REVIEW
  ======================================================= */

  const submitReview = useCallback(
    async () => {
      try {
        console.log(
          "========== REVIEW SUBMIT ==========",
        );

        console.log(
          "Review URL:",
          reviewCreateUrl,
        );

        console.log(
          "Token:",
          token?.token
            ? "Token exists"
            : "No token",
        );

        if (!token?.token) {
          toast.error(
            "Please log in to submit a review",
          );

          return;
        }

        if (!selectedRating) {
          toast.error(
            "Please select a rating",
          );

          return;
        }

        if (!reviewText.trim()) {
          toast.error(
            "Please write a review",
          );

          return;
        }

        if (!displayProduct?._id) {
          toast.error(
            "Product information is missing",
          );

          return;
        }

        setReviewLoading(true);

        /* =========================================
           FORM DATA
        ========================================= */

        const formData = new FormData();

        formData.append(
          "productId",
          displayProduct._id,
        );

        formData.append(
          "productSource",
          "company",
        );

        if (selectedVariation?._id) {
          formData.append(
            "variationId",
            selectedVariation._id,
          );
        }

        formData.append(
          "rating",
          selectedRating,
        );

        formData.append(
          "comment",
          reviewText.trim(),
        );

        reviewImages.forEach(
          (file) => {
            formData.append(
              "images",
              file,
            );
          },
        );

        /* =========================================
           API REQUEST
        ========================================= */

        const response = await fetch(
          reviewCreateUrl,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${token.token}`,
            },

            body: formData,
          },
        );

        console.log(
          "Review HTTP Status:",
          response.status,
        );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              `Failed to submit review (${response.status})`,
          );
        }

        toast.success(
          data?.message ||
            "Review submitted successfully",
        );

        /* =========================================
           OPTIMISTIC REVIEW
        ========================================= */

        const newReview =
          data?.data || {
            _id:
              Date.now().toString(),

            user: {
              firstname:
                token?.firstname ||
                token?.user?.firstname ||
                "You",

              lastname:
                token?.lastname ||
                token?.user?.lastname ||
                "",

              profilePhoto:
                token?.profilePhoto ||
                token?.user?.profilePhoto ||
                null,
            },

            rating:
              selectedRating,

            comment:
              reviewText.trim(),

            images:
              reviewImagePreviews,

            isActive: true,

            isVerifiedPurchase:
              true,

            createdAt:
              new Date().toISOString(),
          };

        setLocalReviews(
          (prev) => [
            newReview,
            ...prev,
          ],
        );

        /* =========================================
           RESET FORM
        ========================================= */

        setSelectedRating(0);

        setReviewText("");

        setReviewImages([]);

        setReviewImagePreviews(
          (prev) => {
            prev.forEach((url) => {
              URL.revokeObjectURL(
                url,
              );
            });

            return [];
          },
        );
      } catch (error) {
        console.error(
          "Review submit error:",
          error,
        );

        toast.error(
          error?.message ||
            "Failed to submit review",
        );
      } finally {
        setReviewLoading(false);
      }
    },
    [
      token,
      selectedRating,
      reviewText,
      reviewImages,
      reviewImagePreviews,
      displayProduct,
      selectedVariation,
      reviewCreateUrl,
    ],
  );

  /* =======================================================
     AVERAGE RATING & TOTAL
  ======================================================= */

  const activeReviews =
    localReviews.filter(
      (r) => r.isActive !== false,
    );

  const totalReviews =
    activeReviews.length;

  const averageRating =
    totalReviews > 0
      ? Number(
          (
            activeReviews.reduce(
              (sum, r) =>
                sum +
                Number(
                  r.rating || 0,
                ),
              0,
            ) / totalReviews
          ).toFixed(1),
        )
      : 0;

  const ratingBreakdown = {
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  };

  activeReviews.forEach((r) => {
    const star = Math.round(
      Number(r.rating),
    );

    if (
      ratingBreakdown[star] !==
      undefined
    ) {
      ratingBreakdown[star]++;
    }
  });

  /* =======================================================
     PRICE
  ======================================================= */

  let currentPrice = null;

  if (
    isPhysical &&
    selectedVariation?.price?.amount != null
  ) {
    currentPrice =
      selectedVariation.price.amount;
  } else if (
    displayProduct?.price?.amount != null
  ) {
    currentPrice =
      displayProduct.price.amount;
  } else if (
    displayProduct?.variations?.[0]?.price
      ?.amount != null
  ) {
    currentPrice =
      displayProduct.variations[0]
        .price.amount;
  }

  /* =======================================================
     IMAGE LIST
  ======================================================= */

  const imageList =
    isPhysical &&
    selectedVariation?.images?.length
      ? selectedVariation.images.filter(
          (img) => hasValue(img),
        )
      : (
          displayProduct?.images || []
        ).filter(
          (img) => hasValue(img),
        );

  /* =======================================================
     PRODUCT MAPPING
  ======================================================= */

  const mappedProduct = {
    _id: displayProduct?._id,

    productName:
      displayProduct?.productName,

    briefDescription:
      displayProduct?.briefDescription,

    benefits: cleanList(
      displayProduct?.benefits,
    ),

    features: cleanList(
      displayProduct?.features,
    ),

    instructionsForUse: cleanList(
      displayProduct?.instructionsForUse,
    ),

    batteryInfo: cleanList(
      displayProduct?.batteryInfo,
    ),

    operatingConditions: cleanList(
      displayProduct?.operatingConditions,
    ),

    packageContents: cleanList(
      displayProduct?.packageContents,
    ),

    precautions: cleanList(
      displayProduct?.precautions,
    ),

    powerRequirements: cleanList(
      displayProduct?.powerRequirements,
    ),

    packageType:
      displayProduct?.packageType,

    delivery:
      displayProduct?.delivery,

    rfidNfcCard:
      displayProduct?.rfidNfcCard,

    activation:
      displayProduct?.activation,

    material:
      displayProduct?.material,

    brandName:
      displayProduct?.brandName,

    manufacturerName:
      displayProduct?.manufacturerName,

    hsnCode:
      displayProduct?.hsnCode,

    duration_days:
      displayProduct?.duration_days,

    productType:
      displayProduct?.productType ||
      "physical",

    weight:
      selectedVariation?.weight,

    dimensions:
      selectedVariation?.dimensions,

    size:
      selectedVariation?.size,

    color:
      selectedVariation?.color,

    quantity:
      displayProduct?.quantity,

    reviews:
      displayProduct?.reviews,
  };

  /* =======================================================
     DIMENSIONS
  ======================================================= */

  let dimensionsLabel = null;

  const d =
    mappedProduct.dimensions;

  if (d) {
    const parts = [];

    if (hasValue(d.length)) {
      parts.push(`L ${d.length}`);
    }

    if (hasValue(d.width)) {
      parts.push(`W ${d.width}`);
    }

    if (hasValue(d.height)) {
      parts.push(`H ${d.height}`);
    }

    dimensionsLabel =
      parts.length > 0
        ? parts.join(" × ")
        : null;
  }

  /* =======================================================
     VARIATION CHANGE
  ======================================================= */

  const handleVariationChange =
    useCallback(
      (variation) => {
        setSelectedVariation(
          variation,
        );

        const imgs = (
          variation?.images || []
        ).filter(
          (img) => hasValue(img),
        );

        if (imgs.length > 0) {
          setSelectedImage(
            imgs[0],
          );
        }
      },
      [],
    );

  /* =======================================================
     SHOW MORE
  ======================================================= */

  const toggleShowMore =
    useCallback((field) => {
      setShowMore((prev) => ({
        ...prev,
        [field]:
          !prev[field],
      }));
    }, []);

  /* =======================================================
     ADD TO CART
  ======================================================= */

  const addToCart = useCallback(
    async () => {
      try {
        if (!token?.token) {
          toast.error(
            "Please log in to add items to cart",
          );

          return;
        }

        const isWalletUser =
          token?.userType ===
          "member";

        if (
          isWalletUser &&
          [
            "686bc4919224b59b776bf19e",
            "686bc5f09224b59b776bf1a8",
            "686d10b27dcf8686e68ebe09",
            "6877a12b7401d33df403bad7",
          ].includes(
            displayProduct?._id,
          )
        ) {
          toast.error(
            "Members who already have a package at signup cannot purchase another package.",
          );

          return;
        }

        const cartUrl =
          isWalletUser
            ? `${base_url}product/addtocartMember`
            : `${base_url}product/addtoCart`;

        const cartBody = {
          productSource:
            "company",

          productId:
            displayProduct?._id,

          subCategoryId:
            subCategory ||
            hierarchy
              .subCategory?._id,

          categoryId:
            category ||
            hierarchy
              .category?._id,

          subSubCategoryId:
            subSubCategory ||
            hierarchy
              .subSubCategory?._id,

          subSubSubCategoryId:
            subSubSubCategory ||
            hierarchy
              .subSubSubCategory?._id,

          variationId:
            selectedVariation?._id,

          quantity: 1,
        };

        const response =
          await fetch(
            cartUrl,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token.token}`,
              },

              body:
                JSON.stringify(
                  cartBody,
                ),
            },
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              "Failed to add to cart",
          );
        }

        toast.success(
          data?.message ||
            "Added to cart!",
        );

        navigate("/cart");
      } catch (error) {
        toast.error(
          error?.message ||
            "Failed to add to cart",
        );
      }
    },
    [
      token,
      displayProduct,
      category,
      subCategory,
      subSubCategory,
      subSubSubCategory,
      selectedVariation,
      hierarchy,
      navigate,
    ],
  );

  /* =======================================================
     LOADING / ERROR
  ======================================================= */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f3faf4] to-white">
        <div className="text-center">
          <div className="h-11 w-11 border-4 border-[#35A93E] border-t-transparent rounded-full animate-spin mx-auto" />

          <p className="text-xs text-gray-500 mt-4">
            Loading product...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-sm">
            Error: {error}
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/shop")
            }
            className="mt-4 bg-[#35A93E] text-white px-5 py-2.5 rounded-xl text-xs font-semibold"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  if (!displayProduct) return null;

  /* =======================================================
     MAIN UI
  ======================================================= */

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f3faf4] via-white to-[#eef9f0] pb-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* TOP NAV */}

        <div className="flex items-center justify-between mb-5">
          <button
            type="button"
            onClick={() =>
              navigate("/shop")
            }
            className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 hover:text-[#35A93E] transition"
          >
            <FaArrowLeft className="text-[10px]" />

            Back to Shop
          </button>

          <div className="text-[10px] text-gray-400">
            Home / Shop / Product
          </div>
        </div>

        {/* PRODUCT TYPE BADGE */}

        <div className="mb-5">
          <span
            className={`inline-flex items-center gap-2 text-[10px] font-bold px-3 py-1.5 rounded-full ${
              isDigital
                ? "bg-blue-50 text-blue-700 border border-blue-100"
                : "bg-green-50 text-[#2d8f35] border border-green-100"
            }`}
          >
            {isDigital ? (
              <>
                <FaShieldAlt />
                Digital Product
              </>
            ) : (
              <>
                <FaBoxOpen />
                Physical Product
              </>
            )}
          </span>
        </div>

        {/* MAIN GRID */}

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">

          {/* ================= LEFT SIDE ================= */}

          <div className="space-y-4">

            {/* MAIN IMAGE */}

            <div
              className={`relative rounded-3xl overflow-hidden border shadow-sm ${
                isDigital
                  ? "bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-blue-100"
                  : "bg-white border-green-100"
              }`}
            >
              <div className="absolute -right-20 -top-20 w-52 h-52 bg-[#35A93E]/5 rounded-full" />

              <div className="absolute -left-20 -bottom-20 w-52 h-52 bg-[#35A93E]/5 rounded-full" />

              <div className="relative min-h-[390px] sm:min-h-[450px] flex items-center justify-center p-8">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={
                      mappedProduct.productName ||
                      "Product"
                    }
                    className="w-full h-full max-h-[430px] object-contain transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="text-gray-300 text-sm">
                    No image available
                  </div>
                )}
              </div>

              <div className="absolute top-5 left-5">
                <span className="inline-flex items-center gap-1.5 bg-[#35A93E] text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-md">
                  <FaCheckCircle />
                  AVAILABLE
                </span>
              </div>
            </div>

            {/* THUMBNAILS */}

            {imageList.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {imageList.map(
                    (img, index) => (
                      <button
                        type="button"
                        key={index}
                        onClick={() =>
                          setSelectedImage(
                            img,
                          )
                        }
                        className={`w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
                          selectedImage ===
                          img
                            ? "border-[#35A93E] shadow-md"
                            : "border-gray-200 hover:border-green-300"
                        }`}
                      >
                        <img
                          src={img}
                          alt=""
                          className="w-full h-full object-contain bg-white p-1"
                        />
                      </button>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* SPECS */}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-[#35A93E] rounded-full" />

                <h3 className="text-sm font-bold text-gray-900">
                  {isDigital
                    ? "Package Details"
                    : "Product Specifications"}
                </h3>
              </div>

              {isDigital ? (
                <div>
                  <DetailRow
                    label="Brand"
                    value={
                      mappedProduct.brandName
                    }
                  />

                  <DetailRow
                    label="Manufacturer"
                    value={
                      mappedProduct.manufacturerName
                    }
                  />

                  <DetailRow
                    label="HSN Code"
                    value={
                      mappedProduct.hsnCode
                    }
                  />

                  <DetailRow
                    label="Package Type"
                    value={
                      mappedProduct.packageType
                    }
                  />

                  <DetailRow
                    label="Delivery"
                    value={
                      mappedProduct.delivery
                    }
                  />

                  <DetailRow
                    label="RFID / NFC"
                    value={
                      mappedProduct.rfidNfcCard
                    }
                  />

                  <DetailRow
                    label="Activation"
                    value={
                      mappedProduct.activation
                    }
                  />

                  {hasValue(
                    mappedProduct.duration_days,
                  ) && (
                    <DetailRow
                      label="Duration"
                      value={`${mappedProduct.duration_days} day${
                        mappedProduct.duration_days >
                        1
                          ? "s"
                          : ""
                      }`}
                    />
                  )}
                </div>
              ) : (
                <div>
                  <DetailRow
                    label="Brand"
                    value={
                      mappedProduct.brandName
                    }
                  />

                  <DetailRow
                    label="Manufacturer"
                    value={
                      mappedProduct.manufacturerName
                    }
                  />

                  <DetailRow
                    label="HSN Code"
                    value={
                      mappedProduct.hsnCode
                    }
                  />

                  <DetailRow
                    label="Material"
                    value={
                      mappedProduct.material
                    }
                  />

                  <DetailRow
                    label="Size"
                    value={
                      mappedProduct.size
                    }
                  />

                  <DetailRow
                    label="Color"
                    value={
                      mappedProduct.color
                    }
                  />

                  {hasValue(
                    mappedProduct.weight,
                  ) && (
                    <DetailRow
                      label="Weight"
                      value={`${mappedProduct.weight} kg`}
                    />
                  )}

                  <DetailRow
                    label="Dimensions"
                    value={
                      dimensionsLabel
                    }
                  />

                  <DetailRow
                    label="Package Type"
                    value={
                      mappedProduct.packageType
                    }
                  />

                  <DetailRow
                    label="Delivery"
                    value={
                      mappedProduct.delivery ||
                      "5–7 business days"
                    }
                  />
                </div>
              )}
            </div>

            {/* =================================================
                WRITE A REVIEW
            ================================================= */}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">

              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-[#35A93E] rounded-full" />

                <h3 className="text-sm font-bold text-gray-900">
                  Write a Review
                </h3>
              </div>

              {/* Rating */}

              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Your Rating
                </p>

                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map(
                    (star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() =>
                          setSelectedRating(
                            star,
                          )
                        }
                        className="transition-transform duration-200 hover:scale-110"
                      >
                        <FaStar
                          className={`text-2xl ${
                            star <=
                            selectedRating
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      </button>
                    ),
                  )}

                  {selectedRating > 0 && (
                    <span className="text-xs text-gray-500 ml-1">
                      {selectedRating}/5
                    </span>
                  )}
                </div>
              </div>

              {/* Review Text */}

              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Your Review
                </p>

                <textarea
                  value={reviewText}
                  onChange={(e) =>
                    setReviewText(
                      e.target.value,
                    )
                  }
                  placeholder="Write your review about this product..."
                  rows={4}
                  maxLength={1000}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none resize-none focus:border-[#35A93E] focus:ring-2 focus:ring-green-100 transition"
                />

                <div className="flex justify-end mt-1">
                  <span className="text-[10px] text-gray-400">
                    {reviewText.length}/1000
                  </span>
                </div>
              </div>

              {/* Image Upload */}

              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Add Photos (optional)
                </p>

                <div className="flex flex-wrap gap-3">

                  {reviewImagePreviews.map(
                    (
                      preview,
                      index,
                    ) => (
                      <div
                        key={index}
                        className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200"
                      >
                        <img
                          src={preview}
                          alt={`Review ${
                            index + 1
                          }`}
                          className="w-full h-full object-cover"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            removeReviewImage(
                              index,
                            )
                          }
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-black/80"
                        >
                          ×
                        </button>
                      </div>
                    ),
                  )}

                  {reviewImages.length <
                    5 && (
                    <label className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#35A93E] hover:bg-green-50 transition">
                      <span className="text-2xl text-gray-400">
                        +
                      </span>

                      <span className="text-[9px] text-gray-400 mt-1">
                        Add
                      </span>

                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={
                          handleReviewImageChange
                        }
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <p className="text-[10px] text-gray-400 mt-2">
                  You can upload up to 5 images
                </p>
              </div>

              {/* Submit */}

              <button
                type="button"
                onClick={submitReview}
                disabled={reviewLoading}
                className={`w-full sm:w-auto px-6 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition ${
                  reviewLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#35A93E] hover:bg-[#2e8b35]"
                }`}
              >
                <FaStar />

                {reviewLoading
                  ? "Submitting..."
                  : "Submit Review"}
              </button>
            </div>

            {/* =================================================
                CUSTOMER REVIEWS
            ================================================= */}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">

              <div className="flex items-center gap-2 mb-5">
                <div className="w-1 h-6 bg-[#35A93E] rounded-full" />

                <h3 className="text-sm font-bold text-gray-900">
                  Customer Reviews
                </h3>
              </div>

              {localReviews.length ===
              0 ? (
                <div className="text-center py-10">
                  <FaStar className="text-4xl text-gray-200 mx-auto mb-3" />

                  <p className="text-sm text-gray-500">
                    No reviews yet
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    Be the first to review this product
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary */}

                  <div className="flex flex-col sm:flex-row gap-6 mb-8 pb-6 border-b border-gray-100">

                    <div className="flex flex-col items-center justify-center min-w-[120px]">

                      <p className="text-4xl font-bold text-gray-900">
                        {averageRating.toFixed(
                          1,
                        )}
                      </p>

                      <div className="flex items-center gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map(
                          (star) => (
                            <FaStar
                              key={star}
                              className={`text-sm ${
                                star <=
                                Math.round(
                                  averageRating,
                                )
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ),
                        )}
                      </div>

                      <p className="text-xs text-gray-500 mt-1">
                        {totalReviews} review
                        {totalReviews !==
                        1
                          ? "s"
                          : ""}
                      </p>
                    </div>

                    <div className="flex-1 space-y-1.5">
                      {[5, 4, 3, 2, 1].map(
                        (star) => {
                          const count =
                            ratingBreakdown[
                              star
                            ] || 0;

                          const percentage =
                            totalReviews >
                            0
                              ? (count /
                                  totalReviews) *
                                100
                              : 0;

                          return (
                            <div
                              key={star}
                              className="flex items-center gap-2 text-xs"
                            >
                              <span className="w-6 text-gray-600 font-medium">
                                {star}★
                              </span>

                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-yellow-400 rounded-full transition-all"
                                  style={{
                                    width: `${percentage}%`,
                                  }}
                                />
                              </div>

                              <span className="w-8 text-right text-gray-500">
                                {count}
                              </span>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>

                  {/* Individual Reviews */}

                  <div className="space-y-6">
                    {localReviews.map(
                      (review) => {
                        const user =
                          review.user ||
                          {};

                        const userName =
                          [
                            user.firstname,
                            user.lastname,
                          ]
                            .filter(
                              Boolean,
                            )
                            .join(" ")
                            .trim() ||
                          "Anonymous";

                        const userInitial =
                          userName
                            .charAt(
                              0,
                            )
                            .toUpperCase();

                        const profilePhoto =
                          user.profilePhoto ||
                          null;

                        return (
                          <div
                            key={
                              review._id ||
                              review.createdAt
                            }
                            className="border-b border-gray-100 pb-6 last:border-0 last:pb-0"
                          >

                            <div className="flex items-start justify-between gap-3 mb-2">

                              <div className="flex items-center gap-3">

                                {/* Avatar */}

                                {profilePhoto ? (
                                  <img
                                    src={
                                      profilePhoto
                                    }
                                    alt={
                                      userName
                                    }
                                    className="w-9 h-9 rounded-full object-cover border border-gray-200"
                                  />
                                ) : (
                                  <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-[#35A93E] font-bold text-sm">
                                    {
                                      userInitial
                                    }
                                  </div>
                                )}

                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {
                                      userName
                                    }
                                  </p>

                                  <div className="flex items-center gap-2 mt-0.5">

                                    <div className="flex items-center gap-0.5">
                                      {[1, 2, 3, 4, 5].map(
                                        (
                                          star,
                                        ) => (
                                          <FaStar
                                            key={
                                              star
                                            }
                                            className={`text-[11px] ${
                                              star <=
                                              review.rating
                                                ? "text-yellow-400"
                                                : "text-gray-300"
                                            }`}
                                          />
                                        ),
                                      )}
                                    </div>

                                    <span className="text-[10px] text-gray-400">
                                      {review.createdAt
                                        ? new Date(
                                            review.createdAt,
                                          ).toLocaleDateString(
                                            "en-IN",
                                            {
                                              day: "numeric",
                                              month:
                                                "short",
                                              year: "numeric",
                                            },
                                          )
                                        : ""}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {review.isVerifiedPurchase && (
                                <span className="text-[10px] bg-green-50 text-[#35A93E] border border-green-100 px-2 py-0.5 rounded-full font-medium">
                                  Verified Purchase
                                </span>
                              )}
                            </div>

                            {review.title && (
                              <p className="text-sm font-semibold text-gray-800 mt-1">
                                {review.title}
                              </p>
                            )}

                            {review.comment && (
                              <p className="text-sm text-gray-700 leading-relaxed mt-2 whitespace-pre-line">
                                {
                                  review.comment
                                }
                              </p>
                            )}

                            {review.images &&
                              review.images
                                .length >
                                0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {review.images.map(
                                    (
                                      img,
                                      idx,
                                    ) => (
                                      <a
                                        key={
                                          idx
                                        }
                                        href={
                                          img
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-[#35A93E] transition"
                                      >
                                        <img
                                          src={
                                            img
                                          }
                                          alt={`Review ${
                                            idx +
                                            1
                                          }`}
                                          className="w-full h-full object-cover"
                                        />
                                      </a>
                                    ),
                                  )}
                                </div>
                              )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ================= RIGHT SIDE ================= */}

          <div className="space-y-4">

            {/* TITLE + RATING */}

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">

              <div className="flex items-start justify-between gap-4">

                <div className="flex-1">

                  <p className="text-[9px] uppercase tracking-[0.18em] text-[#35A93E] font-bold mb-2">
                    LIFEONPLUS HEALTHCARE
                  </p>

                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                    {
                      mappedProduct.productName
                    }
                  </h1>
                </div>

                {hasValue(
                  mappedProduct.brandName,
                ) && (
                  <div className="hidden sm:block text-right">
                    <p className="text-[9px] text-gray-400 uppercase">
                      Brand
                    </p>

                    <p className="text-xs font-bold text-gray-700">
                      {
                        mappedProduct.brandName
                      }
                    </p>
                  </div>
                )}
              </div>

              {hasValue(
                mappedProduct.briefDescription,
              ) && (
                <p className="mt-4 text-sm text-gray-600 leading-6 whitespace-pre-line">
                  {
                    mappedProduct.briefDescription
                  }
                </p>
              )}

              {/* Rating Summary */}

              {totalReviews > 0 && (
                <div className="flex items-center gap-2 mt-4">

                  <div className="flex items-center gap-1 bg-green-50 text-[#35A93E] px-3 py-1.5 rounded-full">

                    <FaStar className="text-yellow-500 text-xs" />

                    <span className="text-xs font-bold">
                      {averageRating.toFixed(
                        1,
                      )}
                    </span>
                  </div>

                  <span className="text-xs text-gray-400">
                    {totalReviews} review
                    {totalReviews !==
                    1
                      ? "s"
                      : ""}
                  </span>
                </div>
              )}
            </div>

            {/* PRICE + CART */}

            <div
              className={`rounded-3xl p-5 border shadow-sm ${
                isDigital
                  ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100"
                  : "bg-gradient-to-br from-green-50 to-white border-green-100"
              }`}
            >

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">

                <div>

                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    {isDigital
                      ? "Package Price"
                      : "Price"}
                  </p>

                  <p className="text-3xl font-bold text-[#167a2c]">

                    {currentPrice != null ? (
                      <>
                        {getCurrencySymbol()}
                        {convertPrice(
                          currentPrice,
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </p>

                  {isDigital &&
                    hasValue(
                      mappedProduct.duration_days,
                    ) && (
                      <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1.5">
                        <FaClock className="text-[#35A93E]" />

                        Valid for{" "}
                        {
                          mappedProduct.duration_days
                        }{" "}
                        day
                        {mappedProduct.duration_days >
                        1
                          ? "s"
                          : ""}
                      </p>
                    )}
                </div>

                <button
                  type="button"
                  onClick={addToCart}
                  className={`w-full sm:w-auto px-6 py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold text-sm text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 ${
                    isDigital
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-[#35A93E] hover:bg-[#2e8b35]"
                  }`}
                >
                  <FaShoppingCart />

                  {isDigital
                    ? "Get Package"
                    : "Add to Cart"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-5">

                <div className="bg-white/80 rounded-xl p-3">
                  <FaCheckCircle className="text-[#35A93E] mb-1" />

                  <p className="text-[9px] text-gray-400">
                    Status
                  </p>

                  <p className="text-xs font-bold text-gray-700">
                    Available
                  </p>
                </div>

                <div className="bg-white/80 rounded-xl p-3">
                  <FaTruck className="text-[#35A93E] mb-1" />

                  <p className="text-[9px] text-gray-400">
                    Delivery
                  </p>

                  <p className="text-xs font-bold text-gray-700">
                    {mappedProduct.delivery ||
                      "5–7 Days"}
                  </p>
                </div>
              </div>
            </div>

            {/* VARIATIONS */}

            {isPhysical && (
              <VariationOptions
                variations={
                  displayProduct?.variations
                }
                selectedVariation={
                  selectedVariation
                }
                onSelectVariation={
                  handleVariationChange
                }
              />
            )}

            {/* DIGITAL BADGES */}

            {isDigital && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex flex-wrap gap-2">

                  {hasValue(
                    mappedProduct.rfidNfcCard,
                  ) && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold bg-blue-50 border border-blue-100 text-blue-700 px-3 py-2 rounded-full">
                      <FaCheckCircle />

                      {
                        mappedProduct.rfidNfcCard
                      }
                    </span>
                  )}

                  {hasValue(
                    mappedProduct.activation,
                  ) && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold bg-blue-50 border border-blue-100 text-blue-700 px-3 py-2 rounded-full">
                      <FaCheckCircle />

                      Activation:{" "}
                      {
                        mappedProduct.activation
                      }
                    </span>
                  )}

                  {hasValue(
                    mappedProduct.delivery,
                  ) && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold bg-blue-50 border border-blue-100 text-blue-700 px-3 py-2 rounded-full">
                      <FaClock />

                      {
                        mappedProduct.delivery
                      }
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* BENEFITS / FEATURES / ETC */}

            <ReadMoreList
              items={
                mappedProduct.benefits
              }
              field="benefits"
              label="Benefits"
              showMore={showMore}
              toggleShowMore={
                toggleShowMore
              }
            />

            {isDigital && (
              <ReadMoreList
                items={
                  mappedProduct.features
                }
                field="features"
                label="Key Features"
                showMore={showMore}
                toggleShowMore={
                  toggleShowMore
                }
              />
            )}

            <ReadMoreList
              items={
                mappedProduct.instructionsForUse
              }
              field="instructionsForUse"
              label="How to Use"
              showMore={showMore}
              toggleShowMore={
                toggleShowMore
              }
            />

            {isPhysical && (
              <>
                <ReadMoreList
                  items={
                    mappedProduct.packageContents
                  }
                  field="packageContents"
                  label="Package Contents"
                  showMore={showMore}
                  toggleShowMore={
                    toggleShowMore
                  }
                />

                <ReadMoreList
                  items={
                    mappedProduct.batteryInfo
                  }
                  field="batteryInfo"
                  label="Battery Information"
                  showMore={showMore}
                  toggleShowMore={
                    toggleShowMore
                  }
                />

                <ReadMoreList
                  items={
                    mappedProduct.operatingConditions
                  }
                  field="operatingConditions"
                  label="Operating Conditions"
                  showMore={showMore}
                  toggleShowMore={
                    toggleShowMore
                  }
                />

                <ReadMoreList
                  items={
                    mappedProduct.precautions
                  }
                  field="precautions"
                  label="Precautions"
                  showMore={showMore}
                  toggleShowMore={
                    toggleShowMore
                  }
                />

                <ReadMoreList
                  items={
                    mappedProduct.powerRequirements
                  }
                  field="powerRequirements"
                  label="Power Requirements"
                  showMore={showMore}
                  toggleShowMore={
                    toggleShowMore
                  }
                />
              </>
            )}
          </div>
        </div>

        {/* BOTTOM TRUST SECTION */}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">

          <div className="bg-white rounded-2xl border border-green-100 p-4 flex items-center gap-3 shadow-sm">

            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <FaShieldAlt className="text-[#35A93E]" />
            </div>

            <div>
              <p className="text-xs font-bold text-gray-800">
                Trusted Healthcare
              </p>

              <p className="text-[10px] text-gray-400">
                Quality-focused products
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-green-100 p-4 flex items-center gap-3 shadow-sm">

            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <FaTruck className="text-[#35A93E]" />
            </div>

            <div>
              <p className="text-xs font-bold text-gray-800">
                Reliable Delivery
              </p>

              <p className="text-[10px] text-gray-400">
                Delivery information available
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-green-100 p-4 flex items-center gap-3 shadow-sm">

            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <FaCheckCircle className="text-[#35A93E]" />
            </div>

            <div>
              <p className="text-xs font-bold text-gray-800">
                Product Information
              </p>

              <p className="text-[10px] text-gray-400">
                Complete product details
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;